import { Global } from "@emotion/react";
import { Chip, FormHelperText, Grid, Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import CssBaseline from "@mui/material/CssBaseline";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import Typography from "@mui/material/Typography";
import { green, grey } from "@mui/material/colors";
import { styled } from "@mui/material/styles";
import { TargetedCheckInsContext } from "contexts/TargetedCheckIns";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { EmailAddress } from "schemas/email";
import { routes } from "schemas/routes";

import StyledButton from "components/Styled/Button";

const drawerBleeding = 56;

const StyledBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "light" ? "#fff" : grey[800],
}));

const Puller = styled(Box)(({ theme }) => ({
  width: 30,
  height: 6,
  backgroundColor: theme.palette.mode === "light" ? grey[300] : grey[900],
  borderRadius: 3,
  position: "absolute",
  top: 8,
  left: "calc(50% - 15px)",
}));

export default function HalfScreenPreviewCard() {
  const navigate = useNavigate();
  const { actionItem, fetchSingleItemLoading, setActionItem } =
    React.useContext(TargetedCheckInsContext);
  const { actionItemId, outreachContactId, threadId } = useParams();

  const [open, setOpen] = React.useState(false);
  const [fullScreen, setFullScreen] = React.useState(false);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
    if (!newOpen) {
      setActionItem(null);
    }
  };

  React.useEffect(() => {
    if (actionItem?.id) {
      setOpen(true);
    }
  }, [actionItem?.id]);

  const formatContact = (contacts?: EmailAddress[]) => {
    const hasContacts =
      contacts && contacts?.find((x) => x.email && x?.email?.length > 0);
    if (!hasContacts) return "";
    const contact = contacts?.[0];
    const oneContact = contact?.name
      ? `${contact?.name} (${contact.email})`
      : contact.email;
    return (
      <>
        <Chip
          sx={{
            fontSize: 10,
            color: green[500],
            border: `1px solid ${green[100]}`,
          }}
          size="small"
          variant="outlined"
          label={oneContact}
        />
        {contacts?.length > 1 && (
          <FormHelperText sx={{ color: grey[500], fontSize: 10 }}>
            +{contacts?.length - 1} more
          </FormHelperText>
        )}
      </>
    );
  };

  const formattedCCs = formatContact(actionItem?.emailDrafts?.[0]?.ccContacts);
  const formattedTos = formatContact(actionItem?.emailDrafts?.[0]?.toContacts);

  const openFullScreen = (
    actionItemId: number,
    outreachContactId: number,
    threadId: string,
  ) => {
    navigate(
      `/${routes.inboxCheckins}/${actionItemId}/contact/${outreachContactId}/email/${threadId}`,
    );
    setFullScreen(true);
  };

  const shouldOpenFullScreen =
    !fullScreen && outreachContactId && threadId && actionItemId;
  React.useEffect(() => {
    if (shouldOpenFullScreen) {
      setFullScreen(true);
    }
  }, [shouldOpenFullScreen]);

  const getDescription = () => {
    if (actionItem && open) {
      return (
        <>
          Preview email drafts for <strong>{actionItem.brandName}</strong>{" "}
          below.
        </>
      );
    } else if (actionItem && !open) {
      return (
        <>
          Swipe up to preview email drafts for{" "}
          <strong>{actionItem.brandName}</strong>.
        </>
      );
    } else {
      return <>Click on a thread to preview an email</>;
    }
  };

  return (
    <Box>
      <CssBaseline />
      <Global
        styles={{
          ".MuiDrawer-root > .MuiPaper-root": {
            height:
              actionItem && open
                ? "fit-content"
                : `calc(50% - ${drawerBleeding}px)`,
            overflow: "visible",
          },
        }}
      />
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        onOpen={toggleDrawer(true)}
        swipeAreaWidth={drawerBleeding}
        disableSwipeToOpen={false}
        ModalProps={{
          keepMounted: true,
        }}
      >
        <StyledBox
          sx={{
            position: "absolute",
            top: -drawerBleeding,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            visibility: "visible",
            right: 0,
            left: 0,
            boxShadow: actionItem && open ? 0 : 3,
          }}
        >
          {!(actionItem && open) && <Puller />}
          <Typography textAlign="center" sx={{ p: 3, color: "text.secondary" }}>
            {getDescription()}
          </Typography>
        </StyledBox>

        {actionItem && (
          <StyledBox
            sx={{
              p: 3,
              overflow: "auto",
            }}
          >
            <>
              <Puller />
              <Typography
                sx={{
                  fontSize: 18,
                  fontWeight: 700,
                  color: "primary.main",
                  mb: 2,
                }}
              >
                {actionItem?.brandName}
              </Typography>

              {fetchSingleItemLoading ? (
                <>
                  <Skeleton
                    animation="wave"
                    width="50%"
                    height="50px"
                    sx={{ borderRadius: 4, my: -2 }}
                  />
                </>
              ) : (
                <>
                  {formattedTos && (
                    <Grid
                      container
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 0.5 }}
                    >
                      <FormHelperText sx={{ color: grey[500], fontSize: 10 }}>
                        To:
                      </FormHelperText>
                      {formatContact(actionItem?.emailDrafts?.[0]?.toContacts)}
                    </Grid>
                  )}

                  {formattedCCs && (
                    <Grid
                      container
                      alignItems="center"
                      gap={1}
                      sx={{ mb: 0.5 }}
                    >
                      <FormHelperText sx={{ color: grey[500], fontSize: 10 }}>
                        Cc:
                      </FormHelperText>{" "}
                      {formatContact(actionItem?.emailDrafts?.[0]?.ccContacts)}
                    </Grid>
                  )}
                </>
              )}

              <Typography gutterBottom sx={{ mt: 2 }}>
                <strong>{actionItem?.subject}</strong>
              </Typography>

              <Typography sx={{ color: grey[500], mb: 3 }} gutterBottom>
                <Box
                  dangerouslySetInnerHTML={{
                    __html:
                      actionItem?.emailDrafts?.[0]?.body?.substring(0, 350) ||
                      actionItem?.description,
                  }}
                ></Box>
                ...
              </Typography>

              <StyledButton
                disableElevation
                fullWidth
                variant="contained"
                disabled={!actionItem?.id}
                onClick={() =>
                  openFullScreen(
                    actionItem.id,
                    actionItem.outreachContactId,
                    actionItem.gmailThreadId,
                  )
                }
              >
                Open Draft
                <Box
                  component="i"
                  className="fa-regular fa-arrow-right"
                  sx={{ ml: 1 }}
                />
              </StyledButton>
            </>
          </StyledBox>
        )}
      </SwipeableDrawer>
    </Box>
  );
}
